import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { NOT_FOUND as HTTP_NOT_FOUND } from '@atlassian/jira-common-constants/src/http-status-codes.tsx';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { fallbackValue, propertyKey, UserPreference } from './constants.tsx';
import type { State } from './types.tsx';

const initialise =
	(
		accountId: string,
		createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
	): Action<State> =>
	async ({ setState, getState }) => {
		const state = getState();

		if (!state.isInitialised && accountId !== null) {
			setState({ isLoading: true });
			await getUserProperty<UserPreference>(accountId, propertyKey)
				.then((value) => {
					setState({
						isInitialised: true,
						data: value === UserPreference.RequestFormFields,
						isLoading: false,
					});
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						'getRequestTypesLayoutPreference succeeded',
						{},
					);
				})
				.catch((error) => {
					if (error.statusCode === HTTP_NOT_FOUND) {
						// user preference has not been set
						setState({ isLoading: false, data: fallbackValue });
						fireOperationalAnalytics(
							createAnalyticsEvent({}),
							'getRequestTypesLayoutPreference succeeded',
							{},
						);
					} else {
						setState({ isLoading: false });
						fireErrorAnalytics({
							meta: {
								id: 'getRequestTypesLayoutPreference',
								packageName: 'jiraIssueCreate',
								teamName: 'gryffindor',
							},
							error,
							attributes: {
								isClientFetchError: isClientFetchError(error),
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					}
				});
		}
	};

const setPreference =
	(
		accountId: string,
		isEnabled: boolean,
		createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
		callback: () => void,
	): Action<State> =>
	async ({ setState, getState }) => {
		const { isLoading, data: oldValue } = getState();
		if (accountId && !isLoading) {
			setState({ data: isEnabled, isLoading: true });

			await setUserProperties(
				accountId,
				propertyKey,
				JSON.stringify(isEnabled ? UserPreference.RequestFormFields : UserPreference.ScreenFields),
			)
				.then((res: Response) => {
					if (!res.ok) {
						return res.text().then((errResp: string) => {
							throw new FetchError(res.status, errResp);
						});
					}
					setState({ data: isEnabled, isLoading: false });
					fireTrackAnalytics(
						createAnalyticsEvent({}),
						'setRequestTypesLayoutPreference succeeded',
						{
							isEnabled,
						},
					);
					return callback();
				})
				.catch((error) => {
					setState({ data: oldValue, isLoading: false });
					fireErrorAnalytics({
						meta: {
							id: 'setRequestTypesLayoutPreference',
							packageName: 'jiraIssueCreate',
							teamName: 'gryffindor',
						},
						error,
						attributes: {
							isClientFetchError: isClientFetchError(error),
						},
						sendToPrivacyUnsafeSplunk: true,
					});
				});
		}
	};

export const actions = { setPreference, initialise } as const;
